import React, { useState } from 'react'
import { CheckCircle, XCircle, Clock, AlertCircle, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Progress } from '@/components/ui/progress'
import type { EvaluationResult } from '@shared/types/evaluation'

interface EvaluationResultsViewProps {
  results: EvaluationResult[]
  runSummary?: {
    totalCases: number
    completedCases: number
    passedCases: number
    failedCases: number
    averageScore: number
    passingScore: number
    status: string
  }
}

export const EvaluationResultsView: React.FC<EvaluationResultsViewProps> = ({ 
  results, 
  runSummary 
}) => {
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set())
  const [showOnlyFailed, setShowOnlyFailed] = useState(false)

  const toggleExpanded = (resultId: string) => {
    const newExpanded = new Set(expandedResults)
    if (newExpanded.has(resultId)) {
      newExpanded.delete(resultId)
    } else {
      newExpanded.add(resultId)
    }
    setExpandedResults(newExpanded)
  }

  const getStatusIcon = (passed: boolean) => {
    return passed ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    )
  }

  const getScoreColor = (score: number, passingScore: number = 0.7) => {
    if (score >= passingScore) return 'text-green-600'
    if (score >= passingScore * 0.7) return 'text-yellow-600'
    return 'text-red-600'
  }

  const filteredResults = showOnlyFailed ? results.filter(r => !r.passed) : results

  if (results.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Results Available</h3>
            <p className="text-muted-foreground">
              Run an evaluation to see detailed results here.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Debug: Log the first result to see the structure
  console.log('First result structure:', results[0])

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      {runSummary && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge 
                variant={runSummary.status === 'COMPLETED' ? 'default' : 
                        runSummary.status === 'FAILED' ? 'destructive' : 'secondary'}
              >
                {runSummary.status}
              </Badge>
              Evaluation Summary
            </CardTitle>
            <CardDescription>
              Performance overview and key metrics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {runSummary.passedCases}
                </div>
                <div className="text-sm text-muted-foreground">Passed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {runSummary.failedCases}
                </div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {runSummary.averageScore.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground">Avg Score</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {((runSummary.passedCases / runSummary.totalCases) * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-muted-foreground">Pass Rate</div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{runSummary.completedCases} / {runSummary.totalCases}</span>
              </div>
              <Progress value={(runSummary.completedCases / runSummary.totalCases) * 100} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">
            Test Case Results ({filteredResults.length})
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowOnlyFailed(!showOnlyFailed)}
            className="gap-2"
          >
            {showOnlyFailed ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {showOnlyFailed ? 'Show All' : 'Show Failed Only'}
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpandedResults(new Set(filteredResults.map(r => r.id)))}
          >
            Expand All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpandedResults(new Set())}
          >
            Collapse All
          </Button>
        </div>
      </div>

      {/* Results List */}
      <div className="space-y-4">
        {filteredResults.map((result, index) => (
          <Card key={result.id}>
            <Collapsible
              open={expandedResults.has(result.id)}
              onOpenChange={() => toggleExpanded(result.id)}
            >
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(result.passed)}
                      <div>
                        <CardTitle className="text-base">
                          Test Case #{index + 1}
                        </CardTitle>
                        <CardDescription>
                          Score: {(result.judgeScore || 0).toFixed(2)} • 
                          Latency: {result.latencyMs}ms
                        </CardDescription>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={result.passed ? 'default' : 'destructive'}
                        className="text-xs"
                      >
                        {result.passed ? 'PASSED' : 'FAILED'}
                      </Badge>
                      <div className={`text-lg font-bold ${getScoreColor(result.judgeScore || 0)}`}>
                        {(result.judgeScore || 0).toFixed(1)}
                      </div>
                      {expandedResults.has(result.id) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-2">
                      Question
                    </div>
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm">{result.question}</p>
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-2">
                      Expected Answer
                    </div>
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm">{result.goldenAnswer}</p>
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-2">
                      RAG Response
                    </div>
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm">{result.ragResponse}</p>
                    </div>
                  </div>
                  
                  {/* Judge Reasoning - handle different possible field names */}
                  {(() => {
                    const reasoning = result.reasoning
                    if (reasoning && reasoning.trim() !== '') {
                      return (
                        <div>
                          <div className="text-sm font-medium text-muted-foreground mb-2">
                            Judge Reasoning
                          </div>
                          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-3 rounded-lg">
                            <p className="text-sm text-blue-900 dark:text-blue-100 whitespace-pre-wrap">
                              {reasoning}
                            </p>
                          </div>
                        </div>
                      )
                    } else {
                      // Show what fields are available for debugging
                      return (
                        <div>
                          <div className="text-sm font-medium text-muted-foreground mb-2">
                            Judge Reasoning
                          </div>
                          <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 p-3 rounded-lg">
                            <p className="text-sm text-yellow-900 dark:text-yellow-100">
                              No reasoning available. Available fields: {Object.keys(result).join(', ')}
                            </p>
                          </div>
                        </div>
                      )
                    }
                  })()}
                  
                  <div className="flex items-center gap-4 pt-2 border-t">
                    <div className="text-sm">
                      <span className="font-medium">Score:</span> {(result.judgeScore || 0).toFixed(2)}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Latency:</span> {result.latencyMs}ms
                    </div>
                    {result.confidence && (
                      <div className="text-sm">
                        <span className="font-medium">Confidence:</span> {result.confidence.toFixed(2)}
                      </div>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ))}
      </div>
    </div>
  )
}